/**
 * Issue-paneel (Joris, 9 okt 2026: "meer context op de issuelijst, zoals een mail"). Opent als drawer vanuit
 * de issuelijst: status, toelichting, en context — een mail uit je eigen mailbox (als momentopname, zodat
 * deelnemers zonder die mail hem ook kunnen lezen), bijlagen (kiezen, slepen of een schermafdruk plakken met
 * ⌘V) en links. Zichtbaarheid bepaalt de server: wie het issue niet mag zien krijgt 404, ook voor bijlagen.
 *
 * Rust: één accentkleur, secundaire formulieren pas na een klik, geen randen waar witruimte volstaat.
 * Vangnetten: toelichting en status → ongedaan maken; context verwijderen → bevestig.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Collapse, Drawer, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import NotitieEditor from '../../ui-components/NotitieEditor';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { bestandenUit, bijlageFout, issueContextApi as api, naamVoorPlakken } from './issueContextApi';

const ICOON = { mail: '✉', bijlage: '📎', link: '🔗' };
const grijs = '#64748b';
const datumTijd = (iso) => (iso ? new Date(iso).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} kB`);
const domein = (url) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; } };
const STATUSSEN = [['open', 'Open'], ['opgelost', 'Opgelost'], ['geparkeerd', 'Geparkeerd']];

/** Kleine telling voor in de lijst: "📎 2 · ✉ 1". Leeg = niets. */
export function contextTelling(t) {
  if (!t) return '';
  return [t.bijlage ? `📎 ${t.bijlage}` : '', t.mail ? `✉ ${t.mail}` : '', t.link ? `🔗 ${t.link}` : ''].filter(Boolean).join(' · ');
}

/** Eerste niet-lege regel van de toelichting, zonder markdown-tekens. */
export function eersteRegel(tekst) {
  const regel = (tekst || '').split('\n').map((r) => r.replace(/[#>*_`]/g, '').replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').trim()).find(Boolean) || '';
  return regel.length > 140 ? `${regel.slice(0, 139)}…` : regel;
}

function MailZoeken({ issueId, onGekoppeld }) {
  const [f, setF] = useState({ q: '', van: '', vanaf: '', tot: '' });
  const [res, setRes] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');
  const zoek = async () => {
    setBezig(true); setFout('');
    try { setRes((await api.mailZoek(issueId, f)).resultaten || []); } catch (e) { setFout(e.message); setRes(null); } finally { setBezig(false); }
  };
  const koppel = async (m) => {
    try { await api.mailKoppel(issueId, m.id); toast.success('Mail gekoppeld'); onGekoppeld(); } catch (e) { toast.error(e.message); }
  };
  const veld = (k, label, extra = {}) => (
    <TextField size="small" label={label} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); zoek(); } }} {...extra} />
  );
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 12, color: grijs, mb: 1 }}>Zoekt alleen in jouw eigen mailbox. Je bewaart een kopie van de mail, zodat iedereen in dit overleg hem kan lezen.</Typography>
      <Stack spacing={1}>
        {veld('q', 'Onderwerp of tekst', { autoFocus: true })}
        {veld('van', 'Van (naam of adres)')}
        <Stack direction="row" spacing={1}>
          {veld('vanaf', 'Vanaf', { type: 'date', InputLabelProps: { shrink: true }, sx: { flex: 1 } })}
          {veld('tot', 'Tot en met', { type: 'date', InputLabelProps: { shrink: true }, sx: { flex: 1 } })}
        </Stack>
        <Button size="small" variant="outlined" onClick={zoek} disabled={bezig} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>
          {bezig ? 'Zoeken…' : 'Zoeken'}
        </Button>
      </Stack>
      {fout && <Typography sx={{ fontSize: 13, color: '#991b1b', mt: 1 }}>{fout}</Typography>}
      {res && res.length === 0 && <Typography sx={{ fontSize: 13, color: grijs, mt: 1 }}>Geen mails gevonden.</Typography>}
      {res && res.length > 0 && (
        <Stack spacing={0.25} sx={{ mt: 1 }}>
          {res.map((m) => (
            <Stack key={m.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }}>{m.onderwerp}</Typography>
                <Typography noWrap sx={{ fontSize: 12, color: grijs }}>{m.van_naam || m.van_adres} · {datumTijd(m.datum)}</Typography>
              </Box>
              <Button size="small" onClick={() => koppel(m)} sx={{ textTransform: 'none', fontWeight: 700 }}>Koppelen</Button>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function LinkToevoegen({ issueId, onKlaar }) {
  const [url, setUrl] = useState('');
  const [titel, setTitel] = useState('');
  const bewaar = async () => {
    try { await api.link(issueId, url.trim(), titel.trim()); setUrl(''); setTitel(''); toast.success('Link toegevoegd'); onKlaar(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <TextField size="small" label="Link" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus />
      <TextField size="small" label="Titel (optioneel)" placeholder="Bijv. Offerte 2026-118 in Gripp" value={titel} onChange={(e) => setTitel(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && url.trim()) { e.preventDefault(); bewaar(); } }} />
      <Button size="small" variant="outlined" disabled={!/^https?:\/\/\S+/.test(url.trim())} onClick={bewaar} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>Toevoegen</Button>
    </Stack>
  );
}

function ContextRij({ item, issueId, magSchrijven, open, onToggle, onVerwijder }) {
  const d = item.data || {};
  const openBijlage = async () => {
    const ext = (d.naam || '').split('.').pop().toLowerCase();
    const inline = ['pdf', 'png', 'jpg', 'jpeg'].includes(ext);
    const venster = inline ? window.open('', '_blank') : null;   // vóór de await openen, anders blokkeert de browser
    try {
      const { url } = await api.download(issueId, item.id, inline);
      if (venster) { venster.opener = null; venster.location.href = url; } else window.location.assign(url);
    } catch (e) { if (venster) venster.close(); toast.error(e.message); }
  };
  const klik = item.soort === 'mail' ? onToggle : item.soort === 'bijlage' ? openBijlage : null;
  const meta = item.soort === 'mail' ? `${d.van_naam || d.van_adres || ''} · ${datumTijd(d.datum)}`
    : item.soort === 'bijlage' ? kb(d.grootte) : domein(d.url);
  const titel = item.titel || d.naam || d.url;
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
        <Typography sx={{ width: 20, fontSize: 14, textAlign: 'center', color: grijs }} aria-hidden>{ICOON[item.soort]}</Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {item.soort === 'link' ? (
            <Typography component="a" href={d.url} target="_blank" rel="noopener noreferrer" noWrap
              sx={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: '#1e293b', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{titel}</Typography>
          ) : (
            <Typography component="button" type="button" onClick={klik} noWrap aria-expanded={item.soort === 'mail' ? open : undefined}
              sx={{ display: 'block', maxWidth: '100%', p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left', font: 'inherit',
                fontSize: 13.5, fontWeight: 600, color: '#1e293b', '&:hover': { textDecoration: 'underline' } }}>{titel}</Typography>
          )}
          <Typography noWrap sx={{ fontSize: 12, color: grijs }}>{meta}</Typography>
        </Box>
        {magSchrijven && (
          <Tooltip title="Verwijderen"><IconButton size="small" aria-label={`Verwijder ${titel}`} onClick={() => onVerwijder(item)}><DeleteOutlineIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></IconButton></Tooltip>
        )}
      </Stack>
      {item.soort === 'mail' && (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ ml: 3.5, mb: 1, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: grijs, mb: 0.75 }}>Kopie van {datumTijd(d.datum)}, gekoppeld door {item.created_by}</Typography>
            <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#1e293b' }}>{d.tekst}{d.ingekort ? ' …' : ''}</Typography>
            {d.weblink && (
              <Button size="small" href={d.weblink} target="_blank" rel="noopener noreferrer" sx={{ mt: 1, px: 0, textTransform: 'none', fontWeight: 700 }}>Open in Outlook</Button>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

/**
 * props: issueId (null = dicht), onClose, onGewijzigd (lijst/tellingen verversen), onStatus(issue, status) —
 * dezelfde handler als in de lijst (opgelost → bevestigen + besluit vastleggen).
 */
export default function IssueContextPaneel({ issueId, typeId, onClose, onGewijzigd, onStatus }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  const [toelichting, setToelichting] = useState('');
  const [open, setOpen] = useState(null);       // id van de uitgeklapte mail
  const [formulier, setFormulier] = useState(''); // '' | 'mail' | 'link'
  const [uploads, setUploads] = useState(0);
  const bestandRef = useRef(null);
  const actueel = useRef(issueId);       // laat antwoord van een vorig issue nooit dit paneel vullen (Codex 9 okt)
  const tekstVan = useRef(null);         // voor welk issue staat de toelichting in de editor

  const laad = useCallback(async () => {
    if (!issueId) return;
    const voor = issueId;
    try {
      const d = await api.context(voor);
      if (actueel.current !== voor || d?.issue?.id !== voor) return;
      setData(d); setFout('');
      // Alleen bij het openen de editor vullen; verversen na een upload/link mag niet-opgeslagen tekst niet wissen.
      if (tekstVan.current !== voor) { tekstVan.current = voor; setToelichting(d.issue?.toelichting || ''); }
    } catch (e) {
      if (actueel.current !== voor) return;
      setFout(e.status === 404 ? 'Dit issue bestaat niet (meer) of je hebt er geen toegang toe.' : e.message); setData(null);
    }
  }, [issueId]);

  useEffect(() => {
    actueel.current = issueId; tekstVan.current = null;
    setData(null); setFout(''); setOpen(null); setFormulier(''); setToelichting('');
    laad();
  }, [issueId, laad]);

  const magSchrijven = !!data?.mag_schrijven && data?.issue?.id === issueId;
  const issue = data?.issue;
  const gewijzigd = (toelichting || '') !== (issue?.toelichting || '');
  const ververs = async () => { await laad(); onGewijzigd?.(); };

  const bewaarToelichting = async () => {
    const vorige = issue?.toelichting || '';
    try {
      await api.toelichting(issueId, toelichting);
      tekstVan.current = null;   // opgeslagen: de editor volgt weer de server
      await ververs();
      const id = issueId;
      biedOngedaanAan('Toelichting opgeslagen', async () => { await api.toelichting(id, vorige); if (actueel.current === id) { tekstVan.current = null; await ververs(); } }, { eigenKnop: true });
    } catch (e) { toast.error(e.message); }
  };

  const upload = async (files) => {
    if (!magSchrijven || !files.length) return;
    for (const ruw of files) {
      const naam = naamVoorPlakken(ruw);
      const f = bijlageFout({ name: naam, size: ruw.size });
      if (f) { toast.error(f); continue; }
      setUploads((n) => n + 1);
      try { await api.bijlage(issueId, ruw, naam); toast.success(`${naam} toegevoegd`); } catch (e) { toast.error(e.message); } finally { setUploads((n) => n - 1); }
    }
    await ververs();
  };

  // Plakken (⌘V) van een schermafdruk of bestand, ook als de cursor in de toelichting staat: in de capture-fase
  // afvangen zodat de editor het niet als (onbewaarde) afbeelding opneemt. Gewone tekst gaat gewoon door.
  const opPlakken = (e) => {
    const files = bestandenUit(e.clipboardData);
    if (!files.length || !magSchrijven) return;
    e.preventDefault(); e.stopPropagation();
    upload(files);
  };
  const opSlepen = (e) => {
    const files = bestandenUit(e.dataTransfer);
    if (!files.length || !magSchrijven) return;
    e.preventDefault(); e.stopPropagation();
    upload(files);
  };

  const verwijder = async (item) => {
    const ok = await bevestig({
      titel: 'Verwijderen?', knop: 'Verwijderen', gevaarlijk: true,
      tekst: item.soort === 'bijlage' ? `"${item.titel}" wordt definitief verwijderd.` : `"${item.titel}" gaat van dit issue af.`,
    });
    if (!ok) return;
    try { await api.verwijder(issueId, item.id); await ververs(); } catch (e) { toast.error(e.message); }
  };

  const zetStatus = async (_e, status) => {
    if (!status || !issue || status === issue.status || !onStatus || (typeId && data?.type?.id !== typeId)) return;
    await onStatus(issue, status);
    await laad();
  };

  return (
    <Drawer anchor="right" open={!!issueId} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: { xs: 2, sm: 3 } }, onPasteCapture: opPlakken, onDragOver: (e) => e.preventDefault(), onDropCapture: opSlepen, 'data-testid': 'issue-paneel' }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#1e293b', lineHeight: 1.3 }}>{issue?.titel || (fout ? 'Issue' : '…')}</Typography>
          {data?.type && (
            <Typography sx={{ fontSize: 12, color: grijs, mt: 0.5 }}>
              {data.type.naam}{data.type.vertrouwelijk ? ' · alleen zichtbaar voor deelnemers' : ''}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Sluiten" onClick={onClose}><CloseIcon /></IconButton>
      </Stack>

      {fout && <Typography sx={{ mt: 3, fontSize: 14, color: grijs }}>{fout}</Typography>}
      {!data && !fout && <Box sx={{ mt: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>}

      {data && (
        <Stack spacing={3} sx={{ mt: 2.5 }}>
          {magSchrijven && onStatus && (!typeId || data.type?.id === typeId) && (
            <ToggleButtonGroup exclusive size="small" value={issue.status} onChange={zetStatus} aria-label="Status">
              {STATUSSEN.map(([w, l]) => (
                <ToggleButton key={w} value={w} sx={{ textTransform: 'none', fontWeight: 700, px: 1.5 }}>{l}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: grijs, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Toelichting</Typography>
            {magSchrijven ? (
              <>
                <NotitieEditor value={toelichting} onChange={setToelichting} afbeeldingen={false} minHeight={90}
                  placeholder="Wat speelt er? Achtergrond, cijfers, wat je al weet…" />
                {gewijzigd && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" disableElevation onClick={bewaarToelichting} sx={{ textTransform: 'none', fontWeight: 700 }}>Opslaan</Button>
                    <Button size="small" color="inherit" onClick={() => setToelichting(issue.toelichting || '')} sx={{ textTransform: 'none', color: grijs }}>Annuleren</Button>
                  </Stack>
                )}
              </>
            ) : (
              issue.toelichting
                ? <Box sx={{ fontSize: 14, color: '#1e293b', '& p': { my: 0.5 } }}><ReactMarkdown>{issue.toelichting}</ReactMarkdown></Box>
                : <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen toelichting.</Typography>
            )}
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: grijs, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>Context</Typography>
            {data.items.length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Nog geen mails, bijlagen of links.</Typography>}
            {data.items.map((it) => (
              <ContextRij key={it.id} item={it} issueId={issueId} magSchrijven={magSchrijven} open={open === it.id}
                onToggle={() => setOpen(open === it.id ? null : it.id)} onVerwijder={verwijder} />
            ))}
            {uploads > 0 && <Typography sx={{ fontSize: 12, color: grijs, mt: 0.5 }}>Uploaden…</Typography>}

            {magSchrijven && (
              <>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
                  <Button size="small" onClick={() => setFormulier(formulier === 'mail' ? '' : 'mail')} sx={{ textTransform: 'none', fontWeight: 700 }}>＋ Mail</Button>
                  <Button size="small" onClick={() => bestandRef.current?.click()} sx={{ textTransform: 'none', fontWeight: 700 }}>＋ Bijlage</Button>
                  <Button size="small" onClick={() => setFormulier(formulier === 'link' ? '' : 'link')} sx={{ textTransform: 'none', fontWeight: 700 }}>＋ Link</Button>
                </Stack>
                <input ref={bestandRef} type="file" hidden multiple accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx" data-testid="bijlage-kiezen"
                  onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; upload(fs); }} />
                <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>Of plak een schermafdruk (⌘V) of sleep een bestand hierheen. Pdf, png, jpg, docx, xlsx tot 25 MB.</Typography>
                {formulier === 'mail' && <MailZoeken issueId={issueId} onGekoppeld={() => { setFormulier(''); ververs(); }} />}
                {formulier === 'link' && <LinkToevoegen issueId={issueId} onKlaar={() => { setFormulier(''); ververs(); }} />}
              </>
            )}
          </Box>
        </Stack>
      )}
    </Drawer>
  );
}

/** Begin van de ISO-week (maandag 00:00, lokale tijd). */
export function beginVanWeek(nu = new Date()) {
  const d = new Date(nu.getFullYear(), nu.getMonth(), nu.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

/** Inklapbaar lijstje "Opgelost deze week" onder de issuelijst, met terugzetten (en ongedaan maken). */
export function OpgelostDezeWeek({ typeId, verversSleutel, magSchrijven, onOpen, onGewijzigd }) {
  const [issues, setIssues] = useState([]);
  const [uit, setUit] = useState(false);
  const laad = useCallback(() => {
    if (!typeId) return;
    const vanaf = beginVanWeek();
    api.opgelost(typeId).then((rij) => setIssues((rij || []).filter((i) => i.opgelost_at && new Date(i.opgelost_at.endsWith('Z') ? i.opgelost_at : `${i.opgelost_at}Z`) >= vanaf)))
      .catch(() => setIssues([]));
  }, [typeId]);
  useEffect(() => { laad(); }, [laad, verversSleutel]);
  if (!issues.length) return null;
  const terug = async (issue) => {
    try {
      await api.status(issue.id, 'open');
      laad(); onGewijzigd?.();
      biedOngedaanAan('Terug op de lijst', async () => { await api.status(issue.id, 'opgelost'); laad(); onGewijzigd?.(); }, { eigenKnop: true });
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Box sx={{ mt: 1.5 }}>
      <Button size="small" color="inherit" onClick={() => setUit(!uit)} aria-expanded={uit} sx={{ textTransform: 'none', color: grijs, px: 0.5 }}>
        {uit ? '▾' : '▸'} Opgelost deze week ({issues.length})
      </Button>
      <Collapse in={uit}>
        <Stack spacing={0.25} sx={{ pl: 1 }}>
          {issues.map((i) => (
            <Stack key={i.id} direction="row" alignItems="center" spacing={1}>
              <Typography component="button" type="button" onClick={() => onOpen?.(i)}
                sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', p: 0, border: 0, bgcolor: 'transparent', textAlign: 'left', font: 'inherit', cursor: 'pointer', fontSize: 13, color: grijs, textDecoration: 'line-through' }}>{i.titel}</Typography>
              {magSchrijven && <Button size="small" onClick={() => terug(i)} sx={{ textTransform: 'none', minWidth: 0, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>Terug op de lijst</Button>}
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}
